"use client";

import { useEffect, useRef } from "react";

const reasons = [
  {
    title: "Single-Origin Sourcing",
    description: "We source from one trusted farm — not a rotating roster of suppliers. You get the same quality every batch, every time.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="9" r="2.5" stroke="#4A5C6B" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    title: "Transparent Lab Testing",
    description: "Every batch is tested by an independent third-party lab. COAs are public. Nothing hidden, nothing to hide.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M9 3H4a1 1 0 00-1 1v16a1 1 0 001 1h16a1 1 0 001-1V4a1 1 0 00-1-1h-5M9 3v4a1 1 0 001 1h4a1 1 0 001-1V3M9 3h6" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 12l2 2 4-4" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Consistent Batch Standards",
    description: "We don't chase trends or blend inconsistently. Our standards are locked in, so your experience is repeatable.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Free Sample Included",
    description: "Every kilo order includes a free sample of a second line. Explore before you commit to your next order.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M20 12V22H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "30-Day Money-Back Guarantee",
    description: "If you're not satisfied, we'll refund you. No questions, no forms, no hoops. We stand behind every order.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="#4A5C6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function WhySwitchSection() {
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="py-28"
      style={{ backgroundColor: "#EDECEA" }}
    >
      <div className="max-w-container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={(el) => { refs.current[0] = el; }}
          className="reveal mb-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-end"
        >
          <div>
            <span className="section-label mb-4 block">The Difference</span>
            <h2
              className="font-serif font-bold"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(2rem, 3.5vw, 3rem)",
                letterSpacing: "-0.025em",
                lineHeight: "1.1",
                color: "#1F1F1F",
              }}
            >
              Why Experienced Users
              <br />
              <em style={{ color: "#3F5A45" }}>Are Switching.</em>
            </h2>
          </div>
          <p
            className="text-base leading-relaxed"
            style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif", maxWidth: "42ch" }}
          >
            You've been through the rotation. You know what inconsistency feels like. Kratapillar was built by people who got tired of it too.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, i) => (
            <div
              key={reason.title}
              ref={(el) => { refs.current[i + 1] = el; }}
              className={`reveal stagger-${i + 1}`}
            >
              <div
                className="group p-8 h-full transition-all duration-350"
                style={{
                  backgroundColor: "white",
                  border: "1px solid #E0DFDB",
                  transition: "all 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = "#3F5A45";
                  el.style.transform = "translateY(-4px)";
                  el.style.boxShadow = "0 12px 40px rgba(63,90,69,0.1)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = "#E0DFDB";
                  el.style.transform = "translateY(0)";
                  el.style.boxShadow = "none";
                }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center mb-6"
                  style={{ backgroundColor: "rgba(74,92,107,0.06)", borderRadius: "2px" }}
                >
                  {reason.icon}
                </div>
                <h3
                  className="font-serif font-semibold mb-3"
                  style={{
                    fontFamily: "Fraunces, Georgia, serif",
                    fontSize: "1.25rem",
                    letterSpacing: "-0.015em",
                    color: "#1F1F1F",
                  }}
                >
                  {reason.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {reason.description}
                </p>
              </div>
            </div>
          ))}

          {/* CTA card */}
          <div
            ref={(el) => { refs.current[6] = el; }}
            className="reveal stagger-6"
          >
            <div
              className="p-8 h-full flex flex-col justify-between"
              style={{
                backgroundColor: "#1F1F1F",
                border: "1px solid #1F1F1F",
              }}
            >
              <div>
                <h3
                  className="font-serif font-bold text-white mb-3"
                  style={{
                    fontFamily: "Fraunces, Georgia, serif",
                    fontSize: "1.5rem",
                    letterSpacing: "-0.02em",
                    lineHeight: "1.2",
                  }}
                >
                  Ready to make the switch?
                </h3>
                <p
                  className="text-sm leading-relaxed mb-8"
                  style={{ color: "rgba(255,255,255,0.55)", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  Try it with a 30-day guarantee and a free sample on the side.
                </p>
              </div>
              <a
                href="/shop"
                className="btn-primary inline-flex self-start"
                style={{ backgroundColor: "#3F5A45" }}
              >
                Shop 1 Kilo
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}